import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import {
  FiCheck,
  FiMail,
  FiMapPin,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { Spinner } from "react-bootstrap";
import {
  deleteAddress,
  getAddresses,
  getAddressesByPage,
  selectAddress,
  setAddressPageSize,
  setDefaultAddress,
} from "../../actions";
import { RECEIVER } from "../../../constants/types";
import Pagination from "../common/Pagination";

const AddressListRow = ({
  item,
  isSelected,
  onSelect,
  onSetDefault,
  onDelete,
  defaultLoading,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const {
    id,
    name,
    company,
    street1,
    street2,
    city,
    zip_code,
    country,
    email,
    phone,
    is_default,
  } = item;

  return (
    <div
      className={`address-list-item-container d-flex flex-row justify-content-between align-items-center${isSelected ? " active" : ""}`}
      onClick={onSelect}
    >
      <div className="address-list-item d-flex flex-column">
        <h3>
          {name}
          {company ? ` @ ${company}` : null}
          {is_default ? <span className="default-tag">DEFAULT</span> : null}
        </h3>
        <div className="address-line">
          <FiMapPin size={13} className="line-icon" />
          <span>{`${street1}${street2 ? `, ${street2}` : ""}, ${city}, ${zip_code}, ${country}`}</span>
        </div>
        {(email || phone) && (
          <div className="contact-line">
            {email && (
              <div className="contact-item">
                <FiMail size={13} className="line-icon" />
                <span>{email}</span>
              </div>
            )}
            {phone && (
              <div className="contact-item">
                <FiPhone size={13} className="line-icon" />
                <span>{phone}</span>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="address-list-item-actions">
        {!is_default && (
          <a
            onClick={(e) => {
              e.stopPropagation();
              onSetDefault();
            }}
            aria-disabled={defaultLoading}
          >
            {id === defaultLoading ? (
              <Spinner className="text-dark" />
            ) : (
              "Set Default"
            )}
          </a>
        )}
        {showDeleteConfirm ? (
          <div className="delete-address-confirmations">
            <a
              className="confirm"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(() => setShowDeleteConfirm(false));
              }}
            >
              <FiCheck />
            </a>
            <a
              className="deny"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(false);
              }}
            >
              <FiX />
            </a>
          </div>
        ) : (
          <a
            className="delete-address-item"
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteConfirm(true);
            }}
          >
            <FiTrash2 />
          </a>
        )}
      </div>
    </div>
  );
};

const AddressListModal = ({
  addresses,
  loading,
  errors,
  current,
  count,
  pageSize,
  defaultLoading,
  selectedId,
  onClose,
  listType,
  usePickupDestination,
  onPopulateCustomerDetails,
  getAddresses,
  getAddressesByPage,
  setAddressPageSize,
  selectAddress,
  deleteAddress,
  setDefaultAddress,
}) => {
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    getAddresses(pageSize);
  }, [getAddresses, pageSize]);

  const handleSearch = useCallback(
    (value) => {
      setSearchText(value);
      getAddresses(pageSize, value);
    },
    [getAddresses, pageSize],
  );

  const handleSelect = (item) => {
    if (usePickupDestination && listType === RECEIVER) {
      onPopulateCustomerDetails(item);
      onClose();
      return;
    }
    selectAddress(item, listType, onClose);
  };

  let list;
  if (loading) {
    list = <div className="spinner-border text-dark m-auto"></div>;
  } else if (errors) {
    list = <p className="mb-0 text-danger">{errors}</p>;
  } else {
    list = addresses.map((item) => (
      <AddressListRow
        key={item.id}
        item={item}
        isSelected={item.id === selectedId}
        onSelect={() => handleSelect(item)}
        onSetDefault={() =>
          setDefaultAddress(item, () => getAddresses(pageSize, searchText))
        }
        onDelete={(onConfirm) =>
          deleteAddress(item.id, () => {
            getAddresses(pageSize);
            onConfirm();
          })
        }
        defaultLoading={defaultLoading}
      />
    ));
  }

  return (
    <div className="modal col-12 p-0 d-flex justify-content-center justify-content-lg-start aligin-items-center">
      <div className="backdrop" onClick={onClose}></div>
      <div className="modal-content address-list settings-card d-flex col-11 col-lg-7 m-auto">
        <div className="list-head card-section head d-flex flex-row justify-content-between align-items-center">
          <div className="d-flex flex-row align-items-center">
            <h3 className="mb-0">My Saved Addresses</h3>
            <FiRefreshCw
              size={16}
              className="refresh-icon mx-3"
              onClick={() => getAddresses(pageSize, searchText)}
            />
          </div>
          <a onClick={onClose}>
            <FiX size={25} />
          </a>
        </div>
        <div className="list-content card-section last">
          <div className="search-action">
            <input
              autoFocus
              type="text"
              placeholder="Search..."
              onChange={(e) => handleSearch(e.target.value)}
              value={searchText}
            />
            <FiSearch size={25} className="search-icon" />
            <FiX
              size={25}
              className={`clear-icon ${searchText ? "active" : ""}`}
              onClick={() => handleSearch("")}
            />
          </div>
          {list}
        </div>
        <div className="list-content card-section last">
          <Pagination
            current={current}
            count={count}
            pageSize={pageSize}
            getListByPage={(pageNo) =>
              getAddressesByPage(null, pageNo, pageSize, searchText)
            }
            setPageSize={(size) => setAddressPageSize(size)}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ addressList }) => ({
  addresses: addressList.addresses,
  loading: addressList.loading,
  errors: addressList.errors,
  current: addressList.currentPage,
  count: addressList.addressResponse?.count,
  pageSize: addressList.pageSize,
  defaultLoading: addressList.defaultToggleLoading,
});

export default connect(mapStateToProps, {
  getAddresses,
  getAddressesByPage,
  setAddressPageSize,
  selectAddress,
  deleteAddress,
  setDefaultAddress,
})(AddressListModal);
